<template>
  <transition name="modal-fade">
    <div
      v-if="show"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
      @click.self="$emit('close')"
    >
      <div
        class="bg-white rounded-[16px] w-full max-w-[540px] max-h-[90vh] overflow-y-auto"
        @click.stop
      >
        <div class="px-6 pt-10 pb-6">
          <!-- Header -->
          <h2 class="text-[20px] font-semibold text-[#1a1a1a] mb-6">
            Book a Session with {{ mentorName }}
          </h2>

          <!-- Date and Time Selection -->
          <div class="mb-6">
            <label class="text-[14px] text-[#667085] mb-3 block">
              Select Preferred Date and Time
            </label>

            <!-- Date Range Picker -->
            <div
              class="bg-white border border-[#E5E7EB] rounded-[12px] p-4 mb-4"
            >
              <div class="flex items-center justify-between mb-4">
                <span class="text-[14px] font-semibold text-[#1a1a1a]"
                  >Date range picker</span
                >
                <div class="flex items-center gap-3">
                  <button
                    type="button"
                    @click="previousMonth"
                    class="p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label="Previous month"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12.5 15L7.5 10L12.5 5"
                        stroke="#667085"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  <span class="text-[14px] font-medium text-[#1a1a1a]">
                    {{ currentMonthYear }}
                  </span>
                  <button
                    type="button"
                    @click="nextMonth"
                    class="p-1 hover:bg-gray-100 rounded transition-colors"
                    aria-label="Next month"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M7.5 15L12.5 10L7.5 5"
                        stroke="#667085"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <!-- Calendar Grid -->
              <div class="grid grid-cols-7 gap-2">
                <!-- Weekday Headers -->
                <div
                  v-for="day in weekDays"
                  :key="day"
                  class="text-center text-[12px] font-medium py-2"
                  :class="
                    day === 'Thu'
                      ? 'text-black font-semibold'
                      : 'text-[#667085]'
                  "
                >
                  {{ day }}
                </div>

                <!-- Calendar Days -->
                <button
                  v-for="(day, index) in calendarDays"
                  :key="index"
                  type="button"
                  :disabled="!day.currentMonth"
                  @click="selectDate(day)"
                  class="aspect-square flex items-center justify-center text-[14px] rounded-[8px] transition-colors"
                  :class="[
                    day.currentMonth ? 'hover:bg-gray-100' : 'invisible',
                    day.selected
                      ? 'bg-black text-white hover:bg-gray-800'
                      : 'text-[#1a1a1a]',
                    !day.currentMonth ? 'opacity-0' : '',
                  ]"
                >
                  {{ day.date }}
                </button>
              </div>
            </div>

            <!-- Time Selection -->
            <div class="mb-4">
              <label class="text-[14px] text-[#667085] mb-3 block"
                >Select Time</label
              >
              <div class="space-y-2">
                <button
                  v-for="time in availableTimes"
                  :key="time"
                  type="button"
                  @click="selectedTime = time"
                  class="w-full py-3 px-4 text-[14px] font-medium rounded-[8px] transition-colors"
                  :class="
                    selectedTime === time
                      ? 'bg-[#F3F4F6] text-[#1a1a1a] border border-[#E5E7EB]'
                      : 'bg-white text-[#667085] hover:bg-[#F9FAFB]'
                  "
                >
                  {{ time }}
                </button>
              </div>
            </div>

            <!-- Confirm Time Button -->
            <button
              v-if="selectedTime"
              type="button"
              class="w-full py-3 text-[14px] font-medium text-[#667085] bg-white border border-[#E5E7EB] rounded-[8px] hover:bg-[#F9FAFB] transition-colors"
            >
              Select {{ selectedTime }} - {{ getEndTime }}
            </button>
          </div>

          <!-- Description -->
          <div class="mb-6">
            <label class="text-[14px] text-[#667085] mb-2 block"
              >Description</label
            >
            <textarea
              v-model="description"
              placeholder="What would you like your mentor to know ahead of time?"
              rows="4"
              class="w-full px-4 py-3 text-[14px] text-[#1a1a1a] bg-[#F9FAFB] border border-[#E5E7EB] rounded-[8px] focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent resize-none placeholder:text-[#D1D5DB]"
            ></textarea>
          </div>

          <!-- Action Buttons -->
          <div class="flex items-center justify-end gap-3">
            <button
              type="button"
              @click="$emit('close')"
              class="px-6 py-2.5 text-[14px] font-medium text-[#667085] bg-white border border-[#E5E7EB] rounded-[8px] hover:bg-[#F9FAFB] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              @click="handleRequestSession"
              :disabled="!canSubmit || submitting"
              class="px-6 py-2.5 text-[14px] font-medium text-white bg-black rounded-[8px] hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {{ submitting ? "Requesting..." : "Request Session" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import apiClient from "@/axios";

export default {
  name: "BookSessionModal",
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    projectId: {
      type: [String, Number],
      required: true,
    },
    mentorName: {
      type: String,
      default: "Mentor",
    },
  },
  data() {
    return {
      currentDate: new Date(),
      selectedDate: null,
      selectedTime: null,
      description: "",
      weekDays: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      availableTimes: ["09:00", "10:00", "11:00", "14:00", "15:00", "16:00"],
      submitting: false,
    };
  },
  computed: {
    currentMonthYear() {
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      return `${
        months[this.currentDate.getMonth()]
      } ${this.currentDate.getFullYear()}`;
    },
    calendarDays() {
      const year = this.currentDate.getFullYear();
      const month = this.currentDate.getMonth();

      const firstDay = new Date(year, month, 1).getDay();
      const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1;

      const lastDate = new Date(year, month + 1, 0).getDate();

      const days = [];

      for (let i = 0; i < adjustedFirstDay; i++) {
        days.push({ date: "", currentMonth: false, selected: false });
      }

      for (let date = 1; date <= lastDate; date++) {
        const isSelected =
          this.selectedDate &&
          this.selectedDate.getDate() === date &&
          this.selectedDate.getMonth() === month &&
          this.selectedDate.getFullYear() === year;

        days.push({
          date,
          currentMonth: true,
          selected: isSelected,
          fullDate: new Date(year, month, date),
        });
      }

      return days;
    },
    canSubmit() {
      return this.selectedDate && this.selectedTime;
    },
    getEndTime() {
      if (!this.selectedTime) return "";
      const [hours, minutes] = this.selectedTime.split(":").map(Number);
      const endHours = (hours + 1) % 24;
      return `${String(endHours).padStart(2, "0")}:${String(minutes).padStart(
        2,
        "0",
      )}`;
    },
  },
  methods: {
    previousMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() - 1,
        1,
      );
    },
    nextMonth() {
      this.currentDate = new Date(
        this.currentDate.getFullYear(),
        this.currentDate.getMonth() + 1,
        1,
      );
    },
    selectDate(day) {
      if (!day.currentMonth) return;
      this.selectedDate = day.fullDate;
    },
    async handleRequestSession() {
      if (!this.canSubmit || this.submitting) return;

      try {
        this.submitting = true;

        const startTime = this.selectedTime;

        const [hours, minutes] = startTime.split(":").map(Number);
        const endHours = (hours + 1) % 24;
        const endTime = `${String(endHours).padStart(2, "0")}:${String(
          minutes,
        ).padStart(2, "0")}`;

        const formattedDate = this.selectedDate.toISOString().split("T")[0];

        const payload = {
          booking_date: formattedDate,
          start_time: startTime,
          end_time: endTime,
          description: this.description || "Learning goal mentorship session",
          mentee_notes: this.description || "",
        };

        const response = await apiClient.post(
          `/web/learning-goals/${this.projectId}/book-mentor-session`,
          payload,
        );

        this.$emit("request-session", response.data);
        this.resetForm();
      } catch (error) {
        console.error("Error booking mentor session:", error);
        const errorMessage =
          error.response?.data?.detail ||
          "Failed to book session. Please try again.";
        this.$toast.error("Booking Failed", errorMessage);
      } finally {
        this.submitting = false;
      }
    },
    resetForm() {
      this.selectedDate = null;
      this.selectedTime = null;
      this.description = "";
      this.currentDate = new Date();
    },
  },
  watch: {
    show(newVal) {
      if (!newVal) {
        this.resetForm();
      }
    },
  },
};
</script>

<style scoped>
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-to,
.modal-fade-leave-from {
  opacity: 1;
}
</style>
